import { FC, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { PickerDay, PickerDayProps } from "@mui/x-date-pickers/PickerDay";
import AppDateCalendar from "@Components/AppDateCalendar/AppDateCalendar";
import Carrier from "src/components/Carrier/Carrier";
import { MdKeyboardArrowDown } from "react-icons/md";
import { Trigger } from "../components";
import { formatDate } from "src/utility/time";

const DATE_FORMAT = "ddd, DD MMM YYYY";

type RangeDayProps = PickerDayProps & { start?: Dayjs; end?: Dayjs };

const RangeDay: FC<RangeDayProps> = ({ day, start, end, outsideCurrentMonth, className, disabled, ...other }) => {
  const date = dayjs(day);
  const isStart = !!start && date.isSame(start, "day");
  const isEnd = !!end && date.isSame(end, "day");
  const inRange = !!start && !!end && date.isAfter(start, "day") && date.isBefore(end, "day");

  return (
    <PickerDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      selected={isStart || isEnd}
      disabled={disabled}
      className={cn(
        className,
        !outsideCurrentMonth && inRange && "bg-background-primary-light! rounded-none! text-font-active!",
        !outsideCurrentMonth && isStart && !!end && "rounded-e-none!",
        !outsideCurrentMonth && isEnd && !!start && "rounded-s-none!",
      )}
    />
  );
};

const OrdersListFiltersMob: FC<{
  from: Dayjs;
  to: Dayjs;
  setDateRange: (range: [Dayjs, Dayjs]) => void;
}> = ({
  from,
  to,
  setDateRange,
}) => {
  const { t } = useTranslation();
  const [month, setMonth] = useState<Dayjs>(from ?? dayjs());
  const [pendingFrom, setPendingFrom] = useState<Dayjs>(from);
  const [pendingTo, setPendingTo] = useState<Dayjs | undefined>(to);

  const handleSelect = (close: () => void) => (date: Dayjs | null) => {
    if (!date) return;

    if (!pendingFrom || pendingTo) {
      setPendingFrom(date);
      setPendingTo(undefined);
      return;
    }

    if (date.isSame(pendingFrom, "day")) return;

    if (date.isBefore(pendingFrom, "day")) {
      setPendingFrom(date);
      setPendingTo(pendingFrom);
      setDateRange([date, pendingFrom]);
      close();
      return;
    }

    setPendingTo(date);
    setDateRange([pendingFrom, date]);
    close();
  };

  return (
    <Carrier
      label={t("UserHistory.ordersList.dateRange")}
      renderTrigger={({ open }) => (
        <Trigger onClick={open}>
          <div className="text-font-default flex items-center text-sm font-bold">
            <span className="flex-1 truncate">{formatDate(from, DATE_FORMAT)}</span>
            <span className="w-4 shrink-0" />
            <span className="flex-1 truncate">{formatDate(to, DATE_FORMAT)}</span>
            <MdKeyboardArrowDown className="text-icon-default w-5 shrink-0" />
          </div>
        </Trigger>
      )}
      renderSheet={({ close }) => (
        <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
          <div className="flex items-stretch gap-2">
            <div className="bg-background-minimal flex w-full flex-col gap-0.5 rounded-md p-3">
              <div className="text-font-minimal text-[11px] font-semibold uppercase tracking-wide">{t("UserHistory.ordersList.from")}</div>
              <div className="text-font-default text-sm font-bold">{formatDate(pendingFrom, DATE_FORMAT)}</div>
            </div>
            <div className="bg-background-minimal flex w-full flex-col gap-0.5 rounded-md p-3">
              <div className="text-font-minimal text-[11px] font-semibold uppercase tracking-wide">{t("UserHistory.ordersList.to")}</div>
              <div className="text-font-default text-sm font-bold">{pendingTo ? formatDate(pendingTo, DATE_FORMAT) : "—"}</div>
            </div>
          </div>

          <AppDateCalendar
            value={month}
            onChange={handleSelect(close)}
            onMonthChange={setMonth}
            disableFuture
            slots={{ day: RangeDay }}
            slotProps={{ day: { start: pendingFrom, end: pendingTo } as any }}
          />
        </div>
      )}
    />
  );
};

export default OrdersListFiltersMob;
